import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Clock, Building2 } from "lucide-react";
import { BRAND, HERO_IMG } from "@/lib/brand";
import { EnquiryForm } from "@/components/site/EnquiryForm";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export function CompanyInfo() {
  const rows = [
    ["Legal name", BRAND.legalName],
    ["Brand", BRAND.name],
    ["Constitution", "Private Limited Company (limited by shares)"],
    ["CIN", BRAND.cin],
    ["GSTIN", BRAND.gstin],
    ["Date of incorporation", BRAND.incorporated],
    ["Directors", BRAND.directors.join(", ")],
    ["Registered office", BRAND.address],
  ];
  return (
    <div className="rounded-3xl border border-black/5 bg-white p-8" data-testid="company-info">
      <p className="flex items-center gap-2 font-display text-lg font-semibold"><Building2 className="h-5 w-5 text-[#FF5A00]" />Company information</p>
      <dl className="mt-6 grid gap-x-8 gap-y-4 sm:grid-cols-2">
        {rows.map(([k, v]) => (
          <div key={k} className={k === "Registered office" ? "sm:col-span-2" : ""}>
            <dt className="text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">{k}</dt>
            <dd className="mt-1 text-sm font-medium" data-testid={`company-${k.toLowerCase().replace(/\s/g, "-")}`}>{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function About() {
  return (
    <div data-testid="about-page">
      <section className="container-x grid gap-12 py-16 lg:grid-cols-2 lg:items-center">
        <div>
          <p className="label-eyebrow">About us</p>
          <h1 className="mt-2 text-4xl font-bold tracking-tighter sm:text-5xl">Self-drive, simplified.</h1>
          <p className="mt-6 text-base leading-7 text-neutral-700">
            {BRAND.name} makes renting a car as easy as booking a cab. Pick from a fleet of recent hatchbacks, sedans, SUVs and 4x4s, see every angle of the car before you book, and pay one transparent price with kilometres and GST included.
          </p>
          <p className="mt-4 text-base leading-7 text-neutral-700">
            Every vehicle is inspected and cleaned before each trip, and your deposit comes back after a simple, photo-documented return check.
          </p>
          <Link to="/cars" data-testid="about-browse" className="mt-8 inline-block rounded-full bg-[#FF5A00] px-7 py-3 font-semibold text-white">Browse cars</Link>
        </div>
        <img src={HERO_IMG} alt="" className="aspect-[4/3] w-full rounded-3xl object-cover" />
      </section>
      <section className="container-x pb-20">
        <p className="mb-6 max-w-2xl text-base leading-7 text-neutral-700">{BRAND.name} is a brand owned and operated by {BRAND.legalName}, a company incorporated under the Companies Act, 2013 and headquartered in {BRAND.city}, {BRAND.state}.</p>
        <CompanyInfo />
      </section>
    </div>
  );
}

export function Contact() {
  const items = [
    { icon: Mail, label: "Email", value: BRAND.email, href: `mailto:${BRAND.email}` },
    { icon: Phone, label: "Phone", value: BRAND.phone, href: `tel:${BRAND.phone.replace(/\s/g, "")}` },
    { icon: MapPin, label: "Registered office", value: BRAND.address },
    { icon: Clock, label: "Support hours", value: BRAND.hours },
  ];
  return (
    <div className="container-x py-16" data-testid="contact-page">
      <p className="label-eyebrow">Contact</p>
      <h1 className="mt-2 text-4xl font-bold tracking-tighter sm:text-5xl">We're here to help.</h1>
      <div className="mt-12 grid gap-8 lg:grid-cols-[1.3fr_1fr]">
      <div className="grid h-fit gap-5 sm:grid-cols-2">
        {items.map((i) => (
          <div key={i.label} className="rounded-3xl border border-black/5 bg-white p-8">
            <i.icon className="h-6 w-6 text-[#FF5A00]" />
            <p className="mt-5 text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">{i.label}</p>
            <p className="mt-1 text-lg font-medium" data-testid={`contact-${i.label.toLowerCase().replace(/\s/g, "-")}`}>{i.href ? <a href={i.href} className="transition-colors duration-200 hover:text-[#FF5A00]">{i.value}</a> : i.value}</p>
          </div>
        ))}
      </div>
      <EnquiryForm type="support" testPrefix="support" cta="Send message"
        fields={[{ key: "name", label: "Full name", required: true }, { key: "phone", label: "Mobile number", required: true, placeholder: "10-digit mobile number" }, { key: "email", label: "Email", type: "email" }, { key: "message", label: "How can we help?", textarea: true, required: true, placeholder: "Booking ID (if any) and your question" }]} />
      </div>
      <p className="mt-8 text-sm text-neutral-600">For complaints, see our <Link to="/legal/grievance" className="text-[#FF5A00] underline">Grievance Redressal</Link> page.</p>
      <div className="mt-10"><CompanyInfo /></div>
    </div>
  );
}

const FAQS = [
  ["What documents do I need?", "A valid driving licence held for at least one year, your Aadhaar card and a live selfie. Upload them once on the Verification page or at checkout; our team verifies them before pickup. Carry your original licence at pickup."],
  ["What is the minimum age to rent?", "The driver must be at least 21 years old."],
  ["Is fuel included?", "No. You receive the car with a recorded fuel or charge level and should return it at the same level."],
  ["How is the price calculated?", "You pay by the hour: the car's hourly rate times the number of hours booked (every started hour counts, minimum 4 hours), plus a ₹99 convenience fee and 18% GST. The refundable security deposit is shown separately."],
  ["Why is my trip more expensive on weekends?", "We use dynamic pricing. Hours that fall on weekends, festival days or daily peak times cost a little more (currently +20%). The exact peak hours and surcharge are shown in your price breakdown before you pay, and the price is locked once you book."],
  ["How many kilometres are included?", "Each car shows its kilometre allowance per 24 hours, prorated to the hours you book. Extra kilometres are charged at the per-km rate shown on the car page."],
  ["When do I get my security deposit back?", "After the return inspection and a 48-hour window for tolls and challans. Refunds usually reflect in 5–7 working days."],
  ["Can I cancel my booking?", "Yes, from My Bookings. Cancel 48+ hours before pickup for a full refund of rental charges, 50% between 24–48 hours. The deposit is always refunded."],
  ["What does Trip protection cover?", "Trip protection is a flat ₹499 per trip, whatever the trip length. It caps your liability for accidental damage at ₹3,000 per incident, subject to the Fuel, Damage & Fines Policy."],
  ["Can I take the car to another state?", "Yes, unless restricted. Any interstate permits or taxes are payable by you."],
];

export function Faq() {
  return (
    <div className="container-x max-w-3xl py-16" data-testid="faq-page">
      <p className="label-eyebrow">FAQ</p>
      <h1 className="mt-2 text-4xl font-bold tracking-tighter sm:text-5xl">Questions, answered.</h1>
      <Accordion type="single" collapsible className="mt-10">
        {FAQS.map(([q, a], i) => (
          <AccordionItem key={q} value={`q${i}`} data-testid={`faq-item-${i}`}>
            <AccordionTrigger className="text-left text-base font-semibold">{q}</AccordionTrigger>
            <AccordionContent className="text-neutral-600 leading-7">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
